import React from "react";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const formatDateInSiteTimezone = (
  dateString: string,
  timezone: string
): string => {
  const date = new Date(dateString);

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    const parts = formatter.formatToParts(date);
    const monthNum = parseInt(
      parts.find((part) => part.type === "month")?.value || "1",
      10
    );
    const day = parseInt(
      parts.find((part) => part.type === "day")?.value || "1",
      10
    );
    const year = parts.find((part) => part.type === "year")?.value || "";
    const hour = parts.find((part) => part.type === "hour")?.value || "12";
    const minutes =
      parts.find((part) => part.type === "minute")?.value || "00";
    const seconds =
      parts.find((part) => part.type === "second")?.value || "00";
    const amPm =
      parts.find((part) => part.type === "dayPeriod")?.value?.toUpperCase() ||
      "AM";

    return `${MONTHS[monthNum - 1]} ${day}, ${year}, ${hour}:${minutes}:${seconds} ${amPm}`;
  } catch {
    return formatDateInUTC(dateString);
  }
};

export const formatClipReceivedLocal = (
  clipHeaderReceived: string | undefined | null,
  clipReceivedLocal: string | undefined | null,
  siteTimezone?: string | null
): string => {
  const utcSource = clipHeaderReceived || clipReceivedLocal;

  if (!utcSource) {
    return "";
  }

  if (siteTimezone) {
    return formatDateInSiteTimezone(utcSource, siteTimezone);
  }

  if (clipReceivedLocal && !clipReceivedLocal.endsWith("Z")) {
    return formatTimeStampWithTimezone(clipReceivedLocal);
  }

  return formatTimeStamp(clipReceivedLocal || utcSource);
};

export const formatTimeStamp = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = MONTHS[date.getUTCMonth()];
  const day = date.getUTCDate();

  // Formatting the hour for 12-hour clock and calculating AM/PM
  let hour = date.getUTCHours();
  const amPm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12; // the hour '0' should be '12'
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const seconds = date.getUTCSeconds().toString().padStart(2, "0");

  return `${month} ${day}, ${year}, ${hour}:${minutes}:${seconds} ${amPm}`;
};

export const formatDateInUTC = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = MONTHS[date.getUTCMonth()];
  const day = date.getUTCDate();

  // Formatting the hour for 12-hour clock and calculating AM/PM in UTC
  let hour = date.getUTCHours();
  const amPm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12; // the hour '0' should be '12'
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const seconds = date.getUTCSeconds().toString().padStart(2, "0");

  return `${month} ${day}, ${year}, ${hour}:${minutes}:${seconds} ${amPm}`;
};

export const formatTimeStampWithTimezone = (dateString: string): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Parse the ISO string to extract the local time components
  const isoString = dateString;
  const date = new Date(isoString);

  // Extract the local time from the original string (before timezone conversion)
  const localTimeMatch = isoString.match(/T(\d{2}):(\d{2}):(\d{2})/);
  const dateMatch = isoString.match(/(\d{4})-(\d{2})-(\d{2})/);

  if (!localTimeMatch || !dateMatch) {
    // Fallback to UTC if parsing fails
    return formatDateInUTC(dateString);
  }

  const year = parseInt(dateMatch[1]);
  const month = parseInt(dateMatch[2]) - 1; // Month is 0-indexed
  const day = parseInt(dateMatch[3]);
  const hour = parseInt(localTimeMatch[1]);
  const minutes = parseInt(localTimeMatch[2]);
  const seconds = parseInt(localTimeMatch[3]);

  // Formatting the hour for 12-hour clock and calculating AM/PM
  let displayHour = hour;
  const amPm = displayHour >= 12 ? "PM" : "AM";
  displayHour = displayHour % 12;
  displayHour = displayHour ? displayHour : 12; // the hour '0' should be '12'
  const displayMinutes = minutes.toString().padStart(2, "0");
  const displaySeconds = seconds.toString().padStart(2, "0");

  return `${months[month]} ${day}, ${year}, ${displayHour}:${displayMinutes}:${displaySeconds} ${amPm}`;
};

export const formatLocalTimeToUTC = (dateString: string): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Parse the ISO string and convert to UTC
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = months[date.getUTCMonth()];
  const day = date.getUTCDate();

  // Formatting the hour for 12-hour clock and calculating AM/PM in UTC
  let hour = date.getUTCHours();
  const amPm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12; // the hour '0' should be '12'
  const minutes = date.getUTCMinutes().toString().padStart(2, "0");
  const seconds = date.getUTCSeconds().toString().padStart(2, "0");

  return `${month} ${day}, ${year}, ${hour}:${minutes}:${seconds} ${amPm}`;
};

export const formatUnixTimestamp = (unixTimestamp: number): string => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Convert Unix timestamp (seconds) to milliseconds for JavaScript Date
  const date = new Date(unixTimestamp * 1000);
  const year = date.getFullYear();
  const month = months[date.getMonth()];
  const day = date.getDate();

  // Formatting the hour for 12-hour clock and calculating AM/PM
  let hour = date.getHours();
  const amPm = hour >= 12 ? "PM" : "AM";
  hour = hour % 12;
  hour = hour ? hour : 12; // the hour '0' should be '12'
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${month} ${day}, ${year}, ${hour}:${minutes}:${seconds} ${amPm}`;
};
